import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import CatalogService, { CatalogItem, CatalogItemKind, CatalogItemPayload } from '../services/CatalogService';
import NumericInput from '../../../components/NumericInput';
import { parseLocaleNumber, formatFixed } from '../../../utils/numberFormatting';
import PrimaryButton from '../../../components/PrimaryButton';
import ScopeSubtasksSection, { DraftScopeItem } from './ScopeSubtasksSection';

interface CatalogItemModalProps {
  item: CatalogItem | null;
  onClose: () => void;
  onSave: () => void;
}

interface FormData {
  item_number: string;
  name: string;
  description: string;
  kind: CatalogItemKind;
  unit: string;
  purchase_price: string;
  default_markup_percent: string;
  sales_price: string;
  tax_rate: string;
  is_active: boolean;
}

const INITIAL_FORM: FormData = {
  item_number: '',
  name: '',
  description: '',
  kind: 'SERVICE',
  unit: 'pcs',
  purchase_price: '',
  default_markup_percent: '',
  sales_price: '',
  tax_rate: '19.00',
  is_active: true,
};

export default function CatalogItemModal({ item, onClose, onSave }: CatalogItemModalProps): React.JSX.Element {
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [salesPricePreview, setSalesPricePreview] = useState<string>('');
  const isEdit = Boolean(item);

  // FE-BILL-R31-1: Draft mode state for new items
  const [hasScope, setHasScope] = useState(false);
  const [draftScopeItems, setDraftScopeItems] = useState<DraftScopeItem[]>([]);
  // Track if we switched to edit mode after partial save failure
  const [createdItemId, setCreatedItemId] = useState<number | null>(null);

  useEffect(() => {
    if (item) {
      setFormData({
        item_number: item.item_number || '',
        name: item.name || '',
        description: item.description || '',
        kind: item.kind,
        unit: item.unit || 'pcs',
        purchase_price: formatFixed(parseFloat(item.purchase_price || '0') || null, 2),
        default_markup_percent: formatFixed(parseFloat(item.default_markup_percent || '0') || null, 2),
        sales_price: formatFixed(parseFloat(item.sales_price || '0') || null, 2),
        tax_rate: formatFixed(parseFloat(item.tax_rate || '19') || 19, 2),
        is_active: item.is_active,
      });
    }
  }, [item]);

  useEffect(() => {
    const purchase = parseLocaleNumber(formData.purchase_price);
    const markup = parseLocaleNumber(formData.default_markup_percent);

    if (purchase !== null && markup !== null) {
      const calculated = CatalogService.calculateSalesPrice(purchase, markup);
      setSalesPricePreview(formatFixed(calculated, 2));
    } else {
      setSalesPricePreview('');
    }
  }, [formData.purchase_price, formData.default_markup_percent]);

  const handleChange = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.item_number.trim()) {
      toast.error('SKU is required');
      return;
    }
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    setSaving(true);
    try {
      const payload: CatalogItemPayload = {
        item_number: formData.item_number.trim(),
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        kind: formData.kind,
        unit: formData.unit.trim(),
        is_active: formData.is_active,
      };

      const purchasePrice = parseLocaleNumber(formData.purchase_price);
      const markup = parseLocaleNumber(formData.default_markup_percent);
      const salesPrice = parseLocaleNumber(formData.sales_price);
      const taxRate = parseLocaleNumber(formData.tax_rate);

      if (purchasePrice !== null) {
        payload.purchase_price = purchasePrice.toFixed(2);
      }
      if (markup !== null) {
        payload.default_markup_percent = markup.toFixed(2);
      }
      if (salesPrice !== null) {
        payload.sales_price = salesPrice.toFixed(2);
      }
      if (taxRate !== null) {
        payload.tax_rate = taxRate.toFixed(2);
      }

      if (isEdit && item) {
        await CatalogService.update(item.id, payload);
        toast.success('Item updated');
        onSave();
      } else if (createdItemId) {
        // FE-BILL-R31-1: Already created, just update
        await CatalogService.update(createdItemId, payload);
        toast.success('Item updated');
        onSave();
      } else {
        // FE-BILL-R31-1: Create new item, then persist draft scope items
        const createdItem = await CatalogService.create(payload);

        // Persist draft scope items if any
        if (hasScope && draftScopeItems.length > 0) {
          let failedCount = 0;

          for (const draftItem of draftScopeItems) {
            try {
              await CatalogService.createScopeItem(createdItem.id, {
                text: draftItem.text,
                sort_order: draftItem.sort_order,
              });
            } catch (scopeErr) {
              console.error('Failed to create scope item:', scopeErr);
              failedCount++;
            }
          }

          if (failedCount > 0) {
            // Partial failure: switch to edit mode
            setCreatedItemId(createdItem.id);
            setDraftScopeItems([]);
            setHasScope(false);
            toast.warning(
              `Katalogleistung erstellt, aber ${failedCount} Subtask(s) konnten nicht gespeichert werden. Bitte nachpflegen.`
            );
            setSaving(false);
            return; // Don't close modal
          }

          toast.success(`Katalogleistung erstellt (inkl. ${draftScopeItems.length} Subtasks)`);
        } else {
          toast.success('Item created');
        }

        onSave();
      }
    } catch (err: any) {
      console.error('Failed to save catalog item:', err);
      const message = err?.response?.data?.detail || err?.response?.data?.message || 'Save failed';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const kindOptions = CatalogService.getKindOptions();

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#021a4a' }}>
            {isEdit ? 'Edit catalog item' : 'New catalog item'}
          </h2>
          <button onClick={onClose} style={closeButtonStyle}>
            <FaTimes size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={formStyle}>
          <div style={bodyStyle}>
            {/* Row: Kind + SKU */}
            <div style={{ display: 'flex', gap: '16px' }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Category *</label>
                <select
                  value={formData.kind}
                  onChange={(e) => handleChange('kind', e.target.value as CatalogItemKind)}
                  style={inputStyle}
                >
                  {kindOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>SKU *</label>
                <input
                  type="text"
                  value={formData.item_number}
                  onChange={(e) => handleChange('item_number', e.target.value)}
                  placeholder="e.g. HEIZ-001"
                  style={inputStyle}
                />
              </div>
            </div>

            {/* Row: Name + Unit */}
            <div style={{ display: 'flex', gap: '16px' }}>
              <div style={{ flex: 2 }}>
                <label style={labelStyle}>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Short name"
                  style={inputStyle}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Unit</label>
                <input
                  type="text"
                  value={formData.unit}
                  onChange={(e) => handleChange('unit', e.target.value)}
                  placeholder="pcs, m2, h..."
                  style={inputStyle}
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label style={labelStyle}>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Longer description (optional)"
                rows={3}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </div>

            {/* FE-BILL-R31-1: Scope Subtasks Section with Draft Mode */}
            {isEdit || createdItemId ? (
              // Edit mode: use API-based section
              <ScopeSubtasksSection catalogItemId={item?.id || createdItemId} />
            ) : (
              // New item: show toggle + draft mode
              <div style={sectionStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: hasScope ? '12px' : '0' }}>
                  <label style={{ ...labelStyle, marginBottom: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={hasScope}
                      onChange={(e) => {
                        setHasScope(e.target.checked);
                        if (!e.target.checked) {
                          setDraftScopeItems([]);
                        }
                      }}
                      style={{ marginRight: '8px' }}
                      data-testid="catalog-scope-toggle"
                    />
                    Hat Leistungsumfang (Subtasks)
                  </label>
                </div>
                {hasScope && (
                  <ScopeSubtasksSection
                    catalogItemId={null}
                    draftMode={true}
                    draftItems={draftScopeItems}
                    onDraftItemsChange={setDraftScopeItems}
                  />
                )}
              </div>
            )}

            {/* Pricing Section */}
            <div style={{ borderTop: '1px solid #e0e3ef', paddingTop: '16px', marginTop: '8px' }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#666' }}>
                Pricing
              </h4>

              {/* Row: Purchase + Markup */}
              <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Purchase price (net)</label>
                  <NumericInput
                    value={formData.purchase_price}
                    onChange={(val) => handleChange('purchase_price', val)}
                    decimals={2}
                    placeholder="0.00"
                    suffix="EUR"
                    min={0}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Markup %</label>
                  <NumericInput
                    value={formData.default_markup_percent}
                    onChange={(val) => handleChange('default_markup_percent', val)}
                    decimals={2}
                    placeholder="0.00"
                    suffix="%"
                    min={0}
                  />
                </div>
              </div>

              {/* Row: Sales Price + VAT */}
              <div style={{ display: 'flex', gap: '16px', marginTop: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>
                    Sales price (net)
                    {salesPricePreview && !formData.sales_price && (
                      <span style={{ color: '#059669', marginLeft: '8px', fontWeight: 400 }}>
                        Preview: {salesPricePreview} EUR
                      </span>
                    )}
                  </label>
                  <NumericInput
                    value={formData.sales_price}
                    onChange={(val) => handleChange('sales_price', val)}
                    decimals={2}
                    placeholder={salesPricePreview || '0.00'}
                    suffix="EUR"
                    min={0}
                  />
                  <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#888' }}>
                    Leave empty to auto-calculate from purchase price + markup
                  </p>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>VAT %</label>
                  <NumericInput
                    value={formData.tax_rate}
                    onChange={(val) => handleChange('tax_rate', val)}
                    decimals={2}
                    placeholder="19.00"
                    suffix="%"
                    min={0}
                    max={100}
                  />
                </div>
              </div>
            </div>

            {/* Active Toggle */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
              <label style={{ ...labelStyle, marginBottom: 0, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => handleChange('is_active', e.target.checked)}
                  style={{ marginRight: '8px' }}
                />
                Active
              </label>
              <span style={{ fontSize: '12px', color: '#888' }}>
                Inactive items won't appear in offer editor
              </span>
            </div>
          </div>

          {/* Footer */}
          <div style={footerStyle}>
            <button type="button" onClick={onClose} style={cancelButtonStyle} disabled={saving}>
              Cancel
            </button>
            <PrimaryButton type="submit" disabled={saving} style={{ padding: '10px 24px' }}>
              {saving ? 'Saving...' : isEdit ? 'Save changes' : 'Create item'}
            </PrimaryButton>
          </div>
        </form>
      </div>
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  background: 'white',
  borderRadius: '12px',
  width: '100%',
  maxWidth: '600px',
  maxHeight: '90vh',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '20px 24px',
  borderBottom: '1px solid #e0e3ef',
};

const closeButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: '#666',
  padding: '4px',
};

const formStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
};

const bodyStyle: React.CSSProperties = {
  padding: '24px',
  overflowY: 'auto',
  flex: 1,
  minHeight: 0, // Required for flex child to scroll properly
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

const footerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
  padding: '16px 24px',
  borderTop: '1px solid #e0e3ef',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '6px',
  fontSize: '13px',
  fontWeight: 600,
  color: '#444',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #e0e3ef',
  borderRadius: '6px',
  fontSize: '14px',
};

// FE-BILL-R31-1: Section style for scope toggle area
const sectionStyle: React.CSSProperties = {
  borderTop: '1px solid #e0e3ef',
  paddingTop: '16px',
  marginTop: '8px',
};

const cancelButtonStyle: React.CSSProperties = {
  padding: '10px 24px',
  border: '1px solid #e0e3ef',
  borderRadius: '10px',
  background: 'white',
  color: '#666',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
};

const saveButtonStyle: React.CSSProperties = {
  padding: '10px 24px',
  border: 'none',
  borderRadius: '10px',
  background: '#021a4a',
  color: 'white',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
};
